import React, { useState } from 'react';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import IconButton from '@mui/material/IconButton';
import MenuIcon from '@mui/icons-material/Menu';
import Box from '@mui/material/Box';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import logo from '../../assets/foster-logo.png';
import logoGuidance from '../../assets/foster-logo-guidance.png';
import Avatar from '@mui/material/Avatar';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import { useNavigate } from 'react-router-dom';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import headerBg from '../../assets/header-bg.png';
import headerBgGuidance from '../../assets/header-bg-guidance.png';
import { signOut } from 'firebase/auth';
import { auth } from '../../firebase';
import { useUser, UserData } from '../../contexts/UserContext';

// Admin user IDs who can access admin features and reports
const ADMIN_USER_IDS = ['01Hy3G8tILYY3wJ4z2481xrGh493', 'bBetdjepqIenXUsp8tnPu1SeH7F3'];
const REPORTS_USER_IDS = ['01Hy3G8tILYY3wJ4z2481xrGh493', 'DXXr7tC2Q7d8h3HFpRrwsOCEtKs2', 'bBetdjepqIenXUsp8tnPu1SeH7F3'];

interface HeaderProps {
  onMenuClick: () => void;
  isAuthenticated: boolean;
  user: UserData | null;
}

const Header: React.FC<HeaderProps> = ({ onMenuClick, isAuthenticated, user }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const menuOpen = Boolean(anchorEl);
  const { user: userData } = useUser();

  const handleProfileMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleProfileNavigation = () => {
    navigate('/my-student-profile');
    handleMenuClose();
  };

  const handleAdminNavigation = () => {
    navigate('/admin');
    handleMenuClose();
  };

  const handleAdminEventsNavigation = () => {
    navigate('/admin/add-edit-events');
    handleMenuClose();
  };

  const handleReferralLinkNavigation = () => {
    if (userData?.isGuidanceUser) {
      navigate('/resources');
    } else {
      navigate('/referral-link');
    }
    handleMenuClose();
  };

  const handleSupportNavigation = () => {
    navigate('/support');
    handleMenuClose();
  };

  const handleAdminSupportNavigation = () => {
    navigate('/admin/support');
    handleMenuClose();
  };

  const handleAdminUsersNavigation = () => {
    navigate('/admin/users');
    handleMenuClose();
  };

  const handleLogout = async () => {
    try {
      if (user) {
        // Firebase logout
        await signOut(auth);
        console.log('Firebase user signed out');
      } else {
        // Fallback to demo logout
        localStorage.setItem('isLoggedOut', 'true');
        sessionStorage.setItem('isLoggedOut', 'true');
        window.dispatchEvent(new Event('authStateChanged'));
      }
      handleMenuClose();
      navigate('/login');
    } catch (error) {
      console.error('Logout error:', error);
      // Fallback to demo logout
      localStorage.setItem('isLoggedOut', 'true');
      sessionStorage.setItem('isLoggedOut', 'true');
      window.dispatchEvent(new Event('authStateChanged'));
    handleMenuClose();
    navigate('/login');
    }
  };

  // Determine which background image to use based on user type
  const backgroundImage = userData?.isGuidanceUser ? headerBgGuidance : headerBg;
  // Determine which logo to use based on user type
  const logoSrc = userData?.isGuidanceUser ? logoGuidance : logo;

  return (
    <AppBar position="fixed" color="primary" elevation={1} sx={{
      backgroundColor: '#7fc400',
      backgroundImage: `url(${backgroundImage})`,
      backgroundRepeat: 'repeat',
      backgroundSize: 'auto',
    }}>
      <Toolbar sx={{ minHeight: { xs: 67, sm: 77 } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
          {isMobile && (
            <IconButton
              edge="start"
              color="inherit"
              aria-label="menu"
              onClick={onMenuClick}
              sx={{ mr: 2 }}
            >
              <MenuIcon />
            </IconButton>
          )}
          <img src={logoSrc} alt="Foster Logo" style={{ height: 40 }} />
        </Box>
        
        {/* User Profile or Login Button */}
        <Box 
          sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 2, 
            mr: isMobile ? 1 : 4,
            // Solid white background for guidance users with padding to expand the area
            backgroundColor: userData?.isGuidanceUser ? 'white' : 'inherit',
            ...(userData?.isGuidanceUser && {
              px: 2,
              py: 1.5,
              borderRadius: 1,
            }),
          }}
        >
          {user ? (
            <Box 
              sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 2, 
                cursor: 'pointer',
                // Solid white background for guidance users
                backgroundColor: userData?.isGuidanceUser ? 'white' : 'inherit',
              }} 
              onClick={handleProfileMenuClick}
            >
              {!isMobile && (
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                  <Typography 
                    variant="body1" 
                    color={userData?.isGuidanceUser ? 'black' : 'inherit'}
                    sx={{ 
                      fontWeight: 500,
                    }}
                  >
                    {userData?.firstName && userData?.lastName ? `${userData.firstName} ${userData.lastName}` : user?.username || user?.email}
                  </Typography>
                  <Typography 
                    variant="caption" 
                    color={userData?.isGuidanceUser ? 'black' : 'inherit'}
                    sx={{ 
                      opacity: userData?.isGuidanceUser ? 1 : 0.9,
                    }}
                  >
                    {userData?.schoolName || user?.email}
                  </Typography>
                </Box>
              )}
              <Avatar 
                src={userData?.userIcon || user?.userIcon || undefined} 
                alt={user?.username || user?.email || 'User'} 
              />
            </Box>
          ) : (
            <Button color="inherit" variant="outlined">Login</Button>
          )}
          <Menu
            anchorEl={anchorEl}
            open={menuOpen}
            onClose={handleMenuClose}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          >
            {ADMIN_USER_IDS.includes(userData?.uid || '') && (
              <MenuItem onClick={handleAdminNavigation}>Admin: Dev</MenuItem>
            )}
            {REPORTS_USER_IDS.includes(userData?.uid || '') && (
              <MenuItem onClick={handleAdminSupportNavigation}>Admin</MenuItem>
            )}
            <MenuItem onClick={handleProfileNavigation}>Profile</MenuItem>
            <MenuItem onClick={handleReferralLinkNavigation}>
              {userData?.isGuidanceUser ? 'Resources' : 'Referral Link'}
            </MenuItem>
            <MenuItem onClick={handleSupportNavigation}>Support</MenuItem>
            <MenuItem onClick={handleLogout}>Logout</MenuItem>
          </Menu>
        </Box>
      </Toolbar>
    </AppBar>
  );
};

export default Header;
